import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Eye, Sparkles, Globe } from "lucide-react";
import { API } from "../Sidebar";

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
};

export const AffiliateSitesPage = () => {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchSites = async () => {
    try {
      const res = await axios.get(`${API}/affiliate/sites`);
      setSites(res.data);
    } catch (e) {
      toast.error("Échec du chargement des sites Affiliate");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSites();
  }, []);

  const deleteSite = async (id, name) => {
    if (!window.confirm(`Supprimer le site "${name}" et toutes ses données ?`)) return;
    try {
      await axios.delete(`${API}/affiliate/sites/${id}`);
      toast.success("Site supprimé");
      fetchSites();
    } catch {
      toast.error("Échec de la suppression");
    }
  };

  return (
    <div data-testid="affiliate-sites-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-5 h-5 text-orange-500" />
            <h2
              className="text-2xl font-bold text-slate-900"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              Affiliate Hub — My Sites
            </h2>
          </div>
          <p className="text-slate-600">
            {sites.length} site{sites.length === 1 ? "" : "s"} SaaS affilié{sites.length === 1 ? "" : "s"}
          </p>
        </div>
        <Link to="/admin/affiliate/builder">
          <Button
            className="bg-orange-500 hover:bg-orange-600"
            data-testid="new-affiliate-site-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau site Affiliate
          </Button>
        </Link>
      </div>

      {loading ? (
        <p className="text-center text-slate-500 py-8">Chargement…</p>
      ) : sites.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Sparkles className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Aucun site Affiliate</h3>
            <p className="text-slate-500 mb-4">
              Lancez votre 1er site SaaS programmatique (style La Stack Prime)
            </p>
            <Link to="/admin/affiliate/builder">
              <Button className="bg-orange-500 hover:bg-orange-600">
                <Plus className="w-4 h-4 mr-2" />
                Créer un site
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden" data-testid="affiliate-sites-list">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-slate-600">
                  <th className="px-4 py-3 font-semibold">Marque</th>
                  <th className="px-4 py-3 font-semibold">Domaine</th>
                  <th className="px-4 py-3 font-semibold">Langue</th>
                  <th className="px-4 py-3 font-semibold">Modifié le</th>
                  <th className="px-4 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sites.map((s) => (
                  <tr
                    key={s.id}
                    className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60"
                    data-testid={`affiliate-site-row-${s.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">{s.name}</span>
                        <Badge variant="outline" className="font-normal">
                          {s.slug}
                        </Badge>
                      </div>
                      {s.tagline && (
                        <div className="text-xs text-slate-500 mt-0.5">{s.tagline}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {s.domain ? (
                        <a
                          href={s.domain}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-orange-600 hover:underline inline-flex items-center gap-1"
                        >
                          <Globe className="w-3 h-3" />
                          {s.domain.replace(/^https?:\/\//, "")}
                        </a>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="secondary" className="uppercase">
                        {s.lang || "fr"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                      {fmt(s.updated_at || s.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/admin/affiliate/sites/${s.id}`)}
                          data-testid={`open-affiliate-site-${s.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Ouvrir
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteSite(s.id, s.name)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          data-testid={`delete-affiliate-site-${s.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};
